import { useCallback, useEffect, useState } from "react";
import { Users, MonitorSmartphone, ShieldBan } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { UsersTab } from "../components/admin/UsersTab";
import { DevicesTab } from "../components/admin/DevicesTab";
import { CriticalGuestsTab } from "../components/admin/CriticalGuestsTab";
import { api, errMsg } from "../lib/api";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [ips, setIps] = useState([]);

  const load = useCallback(async () => {
    try {
      const [u, d, t] = await Promise.all([api.get("/users"), api.get("/devices"), api.get("/trusted-ips")]);
      setUsers(u.data);
      setDevices(d.data);
      setIps(t.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const pending = devices.filter((d) => d.status === "pending").length;

  return (
    <div className="relative z-10 min-h-screen">
      <AppHeader />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rise">
          <div className="eyebrow">Admin panel</div>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-emerald-950">Users, devices & guests</h1>
          <p className="mt-2 text-slate-600">Manage team accounts, reset passwords, approve devices and keep the Critical guest list.</p>
        </div>
        <Tabs defaultValue="users" className="mt-8">
          <TabsList className="h-12 rounded-full bg-white/70 backdrop-blur-xl p-1 border border-white">
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-full px-5 h-10 data-[state=active]:bg-emerald-900 data-[state=active]:text-amber-50 gap-2"><Users className="h-4 w-4" /> Users</TabsTrigger>
            <TabsTrigger value="devices" data-testid="tab-devices" className="rounded-full px-5 h-10 data-[state=active]:bg-emerald-900 data-[state=active]:text-amber-50 gap-2">
              <MonitorSmartphone className="h-4 w-4" /> Devices
              {pending > 0 && <span data-testid="pending-devices-badge" className="ml-1 rounded-full bg-amber-400 text-emerald-950 text-[10px] font-bold px-1.5 py-0.5">{pending}</span>}
            </TabsTrigger>
            <TabsTrigger value="critical" data-testid="tab-critical-guests" className="rounded-full px-5 h-10 data-[state=active]:bg-emerald-900 data-[state=active]:text-amber-50 gap-2"><ShieldBan className="h-4 w-4" /> Critical Guests</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-5"><UsersTab users={users} reload={load} /></TabsContent>
          <TabsContent value="devices" className="mt-5"><DevicesTab devices={devices} ips={ips} reload={load} /></TabsContent>
          <TabsContent value="critical" className="mt-5"><CriticalGuestsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
